'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  OWASP_RISK_FACTOR_KEYS,
  calculateOwaspRisk,
  generateOwaspRiskVector,
  normalizeOwaspRiskFactors,
  parseOwaspRiskVector,
  type OwaspRiskCalculation,
  type OwaspRiskFactorKey,
} from '@terra/shared-cvss';

type OwaspRiskDraftFactors = Record<OwaspRiskFactorKey, number | null>;

type UseOwaspRiskCalculatorOptions = {
  initialScore?: string;
  initialVector?: string;
  onScoreChange: (score: string, vector: string, level: string) => void;
};

const getEmptyFactors = (): OwaspRiskDraftFactors =>
  OWASP_RISK_FACTOR_KEYS.reduce<OwaspRiskDraftFactors>((acc, key) => {
    acc[key] = null;

    return acc;
  }, {} as OwaspRiskDraftFactors);

const buildFactorsFromVector = (vector: string): OwaspRiskDraftFactors => {
  const parsed = parseOwaspRiskVector(vector);

  if (!parsed) {
    return getEmptyFactors();
  }

  return OWASP_RISK_FACTOR_KEYS.reduce<OwaspRiskDraftFactors>((acc, key) => {
    acc[key] = parsed[key];

    return acc;
  }, {} as OwaspRiskDraftFactors);
};

const areAllFactorsSelected = (factors: OwaspRiskDraftFactors): boolean =>
  OWASP_RISK_FACTOR_KEYS.every((key) => factors[key] !== null);

export const useOwaspRiskCalculator = ({
  initialScore,
  initialVector,
  onScoreChange,
}: UseOwaspRiskCalculatorOptions) => {
  const [factors, setFactors] = useState<OwaspRiskDraftFactors>(() =>
    initialVector ? buildFactorsFromVector(initialVector) : getEmptyFactors(),
  );
  const [calculation, setCalculation] = useState<OwaspRiskCalculation | null>(
    null,
  );
  const [calculatedScore, setCalculatedScore] = useState<string>('');
  const [vectorString, setVectorString] = useState<string>('');
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  const onScoreChangeRef = useRef(onScoreChange);
  const lastEmittedRef = useRef<{
    score: string;
    vector: string;
    level: string;
  } | null>(null);

  onScoreChangeRef.current = onScoreChange;

  useEffect(() => {
    if (hasUserInteracted) {
      return;
    }

    if (initialVector) {
      setFactors(buildFactorsFromVector(initialVector));
      setVectorString(initialVector);
    }

    if (initialScore) {
      setCalculatedScore(initialScore);
    }
  }, [hasUserInteracted, initialScore, initialVector]);

  useEffect(() => {
    if (areAllFactorsSelected(factors)) {
      const normalized = normalizeOwaspRiskFactors(
        OWASP_RISK_FACTOR_KEYS.reduce<Record<OwaspRiskFactorKey, number>>(
          (acc, key) => {
            acc[key] = factors[key] ?? 0;

            return acc;
          },
          {} as Record<OwaspRiskFactorKey, number>,
        ),
      );
      const result = calculateOwaspRisk(normalized);
      const vector = generateOwaspRiskVector(normalized);
      const score = String(result.score);
      const level = result.level;

      setCalculation(result);
      setCalculatedScore(score);
      setVectorString(vector);

      if (!hasUserInteracted) {
        return;
      }

      const lastEmitted = lastEmittedRef.current;

      if (
        lastEmitted &&
        lastEmitted.score === score &&
        lastEmitted.vector === vector &&
        lastEmitted.level === level
      ) {
        return;
      }

      lastEmittedRef.current = { score, vector, level };
      onScoreChangeRef.current(score, vector, level);

      return;
    }

    setCalculation(null);
    setCalculatedScore('');
    setVectorString('');
  }, [factors, hasUserInteracted]);

  const handleFactorChange = useCallback(
    (factor: OwaspRiskFactorKey, value: number) => {
      setHasUserInteracted(true);
      setFactors((prev) => ({ ...prev, [factor]: value }));
    },
    [],
  );

  const resetCalculator = useCallback(() => {
    setHasUserInteracted(true);
    setFactors(getEmptyFactors());
    setCalculation(null);
    setCalculatedScore('');
    setVectorString('');
    lastEmittedRef.current = { score: '', vector: '', level: '' };
    onScoreChangeRef.current('', '', '');
  }, []);

  return {
    factors,
    calculation,
    calculatedScore,
    vectorString,
    handleFactorChange,
    resetCalculator,
  };
};
